import { BreakpointObserver } from '@angular/cdk/layout';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Renderer2,
  viewChild,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AppConfigService } from '@app/app-config.service';
import { GraphQLAdminMainDataQuery } from '@app/graphql/.generated';
import { NetworkStore } from '@app/store/network.store';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FeedbackDialogComponent } from '@shared/feedback-dialog/feedback-dialog.component';
import { OAuthService } from 'angular-oauth2-oidc';
import md5 from 'md5';
import { AvatarModule } from 'primeng/avatar';
import { DialogService } from 'primeng/dynamicdialog';
import { MenuModule } from 'primeng/menu';
import { Menubar, MenubarModule } from 'primeng/menubar';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { delay, distinctUntilChanged, of, switchMap } from 'rxjs';

import { MainDataStore } from '@app/store/main-data.store';
import { BadgeModule } from 'primeng/badge';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { EventStore } from '@app/store/event.store';
import { ButtonModule } from 'primeng/button';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MenuItem, MessageService } from 'primeng/api';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AvatarModule,
    StyleClassModule,
    FontAwesomeModule,
    NgOptimizedImage,
    RouterLinkActive,
    RouterLink,
    RippleModule,
    TooltipModule,
    BadgeModule,
    ButtonModule,
    MenubarModule,
    MenuModule,
    NgTemplateOutlet,
  ],
})
export class NavbarComponent {
  readonly httpClient = inject(HttpClient);
  readonly messageService = inject(MessageService);
  readonly mainDataStore = inject(MainDataStore);
  readonly eventStore = inject(EventStore);
  readonly oAuthService = inject(OAuthService);
  readonly router = inject(Router);
  readonly appConfig = inject(AppConfigService);
  readonly renderer = inject(Renderer2);
  readonly dialogService = inject(DialogService);

  hasNewVersion = input.required<boolean>();
  doUpdate = output();

  isOnline = inject(NetworkStore).isOnline;
  isSaving = toSignal(
    toObservable(this.eventStore.isSaving).pipe(
      distinctUntilChanged(),
      switchMap((isSaving) => {
        return isSaving ? of(true) : of(false).pipe(delay(250));
      })
    )
  );

  menubar = viewChild<Menubar>('menubar');

  getBadgeValue(
    ...key: (keyof NonNullable<GraphQLAdminMainDataQuery['administrationData']['acknowledgeableMissingMappings']>)[]
  ): undefined | string {
    if (!this.mainDataStore.hasSuperUserRole()) {
      return undefined;
    }
    const count = key.reduce((sum, k) => sum + this.mainDataStore.acknowledgeableMissingMappings()[k], 0);
    if (count === 0) {
      return undefined;
    }
    return `${count}`;
  }

  isLargeScreen = toSignal(
    inject(BreakpointObserver)
      .observe('(min-width: 960px)')
      .pipe(map((v) => v.matches))
  );

  fullname = this.oAuthService.getIdentityClaims().name as string;
  email = this.oAuthService.getIdentityClaims().email as string | undefined;
  avatarUrl = `https://www.gravatar.com/avatar/${this.email?.length ? md5(this.email.toLowerCase()) : '00000000000000000000000000000000'}?r=g&d=mp`;

  // Super/Key users get the full manual; everyone else gets the user-only variant.
  manualUrl = computed(() => {
    const isFrench = this.mainDataStore.language().startsWith('fr');
    const isSuperOrKey = this.mainDataStore.hasSuperUserOrKeyUserRole();
    if (isFrench) {
      return isSuperOrKey
        ? "assets/manual/2607 Manuel d'utilisation tacsRecording fr.pdf"
        : "assets/manual/2607 Manuel d'utilisation tacsRecording fr user.pdf";
    }
    return isSuperOrKey
      ? 'assets/manual/2604 Handbuch tacs®Recording.pdf'
      : 'assets/manual/2604 Handbuch tacs®Recording User.pdf';
  });

  varHealthcareBaseUri = this.appConfig.configuration?.varHealthcareBaseUri;

  avatarItems = computed(() => {
    return [
      {
        label: $localize`Mandanten`,
        icon: 'sitemap',
        items: this.mainDataStore.tenants().map((tenant) => ({
          label: tenant.name,
          styleClass: tenant.id === this.mainDataStore.tenant()?.id ? 'font-bold' : undefined,
          command: () => this.mainDataStore.setTenant(tenant),
        })),
      },
      {
        label: $localize`System`,
        icon: 'gear',
        items: [
          {
            label: $localize`Test Fehler`,
            command: () => this.throwError(),
            icon: 'gear',
            visible: this.mainDataStore.admin(),
          },
          {
            label: $localize`Feedback`,
            command: () => this.openFeedbackDialog(),
            icon: 'message-smile',
          },
        ],
      },
      {
        label: $localize`Profil`,
        icon: 'user',
        items: [
          {
            label: $localize`Avatar einrichten`,
            url: 'https://de.gravatar.com/',
            icon: 'user',
          },
          {
            label: $localize`Abmelden`,
            command: () => this.logout(),
            disabled: this.isSaving(),
            icon: 'sign-out',
          },
        ],
      },
    ] satisfies MenuItem[];
  });

  languageMenuItems: MenuItem[] = [
    {
      label: $localize`De`,
      url: '/de',
    },
    {
      label: $localize`Fr`,
      url: '/fr',
    },
  ];

  items = computed(
    () =>
      [
        {
          label: $localize`Erfassung`,
          // @ts-expect-error this dirty hack is need to use tacs3 custom icons
          icon: ['tacs3', 'record'] as string,
          routerLink: ['activities'],
          visible: !this.mainDataStore.hasControllingRole(),
        },
        {
          label: $localize`Kalender`,
          icon: 'calendar',
          routerLink: ['overview'],
          visible: !this.mainDataStore.hasControllingRole(),
        },
        this.appConfig.configuration?.featureFlags.Chop &&
        (this.mainDataStore.hasSuperUserOrKeyUserRole() || this.mainDataStore.hasControllingRole())
          ? {
              label: $localize`Analyse`,
              icon: 'bar-chart',
              visible: this.appConfig.configuration?.featureFlags.Bi,
              items: [
                {
                  label: $localize`Auswertungscockpit`,
                  icon: 'bar-chart',
                  routerLink: ['analysis'],
                },
                {
                  label: $localize`CHOP 99.C`,
                  icon: 'notes-medical',
                  routerLink: ['analysis', 'chop'],
                },
              ],
            }
          : {
              label: $localize`Analyse`,
              icon: 'bar-chart',
              routerLink: ['analysis'],
              visible: this.appConfig.configuration?.featureFlags.Bi,
            },
        {
          label: $localize`Personalplanung`,
          icon: 'calendar-circle-user',
          routerLink: ['staff-scheduling'],
          visible: this.appConfig.configuration?.featureFlags.StaffScheduling,
        },
        {
          label: $localize`Administration`,
          icon: 'gear',
          badge: this.getBadgeValue('total'),
          items: [
            {
              label: $localize`Monatsabschluss`,
              icon: 'calendar-check',
              routerLink: ['reports'],
              visible: this.mainDataStore.hasSuperUserOrKeyUserRole(),
            },
            {
              label: $localize`Synchronisierung auslösen`,
              icon: 'rotate',
              command: () => this.triggerSync(),
              visible: this.mainDataStore.hasSuperUserRole(),
            },
            {
              label: $localize`Stammdaten`,
              icon: 'table',
              visible: this.mainDataStore.hasSuperUserOrKeyUserRole(),
              items: [
                {
                  label: $localize`Mitarbeiter:innen`,
                  icon: 'user',
                  routerLink: ['master-data', 'employees'],
                  visible: this.mainDataStore.hasSuperUserOrKeyUserRole(),
                },
                {
                  label: this.mainDataStore.customersLabel(),
                  icon: 'clipboard-user',
                  routerLink: ['master-data', 'customers'],
                  visible: this.mainDataStore.hasSuperUserOrKeyUserRole(),
                },
                {
                  label: $localize`Fall-Umbuchung`,
                  icon: 'arrow-up-arrow-down',
                  routerLink: ['master-data', 'case-rebooking'],
                  visible: this.mainDataStore.admin(),
                },
                {
                  label: $localize`Diensttypen`,
                  icon: 'calendar-day',
                  routerLink: ['master-data', 'service-types'],
                  visible: this.mainDataStore.hasSuperUserRole(),
                },
                {
                  label: $localize`Massnahmen`,
                  icon: 'stethoscope',
                  routerLink: ['master-data', 'measures'],
                  visible: this.mainDataStore.hasSuperUserRole(),
                },
                {
                  label: $localize`Präsenzen`,
                  icon: 'calendar-check',
                  routerLink: ['master-data', 'presence-types'],
                  visible:
                    this.mainDataStore.hasSuperUserRole() && this.appConfig.configuration?.featureFlags.StaffScheduling,
                },
                {
                  label: $localize`Absenzen`,
                  icon: 'table',
                  routerLink: ['master-data', 'absence-types'],
                  visible:
                    this.mainDataStore.hasSuperUserRole() && this.appConfig.configuration?.featureFlags.StaffScheduling,
                },
                {
                  label: $localize`Zeitprofile`,
                  icon: 'clock',
                  routerLink: ['master-data', 'time-profiles'],
                  visible:
                    this.mainDataStore.hasSuperUserRole() &&
                    !this.appConfig.configuration?.featureFlags.ExternalSystemPolypoint,
                },
                {
                  label: $localize`Mandanten`,
                  icon: 'sitemap',
                  routerLink: ['master-data', 'tenants'],
                  visible: this.mainDataStore.admin(),
                },
                {
                  label: $localize`Technische Benutzer`,
                  icon: 'user-robot',
                  routerLink: ['master-data', 'service-accounts'],
                  visible: this.mainDataStore.admin(),
                },
                {
                  label: $localize`Allgemeine Einstellungen`,
                  icon: 'gear',
                  routerLink: ['master-data', 'configs'],
                  visible: this.mainDataStore.admin(),
                },
                {
                  label: $localize`Events`,
                  icon: 'rectangle-history',
                  routerLink: ['master-data', 'events'],
                  visible: this.mainDataStore.admin(),
                },
              ],
            },
            {
              label: $localize`Gruppen`,
              icon: 'cubes',
              items: [
                {
                  label: this.mainDataStore.customerLabel(),
                  icon: 'user-group',
                  routerLink: ['master-data', 'customer-groups'],
                },
                {
                  label: this.mainDataStore.employeeLabel(),
                  icon: 'user-group',
                  routerLink: ['master-data', 'employee-groups'],
                },
                {
                  label: $localize`Bett/Zimmer`,
                  icon: 'bed',
                  routerLink: ['master-data', 'bed-room-groups'],
                },
                {
                  label: $localize`Variablen Sets`,
                  icon: 'list-check',
                  routerLink: ['master-data', 'variable-sets'],
                  visible: this.appConfig.configuration?.featureFlags.VariableSets,
                },
              ],
            },
            {
              label: $localize`Fremdsysteme`,
              visible: this.mainDataStore.hasSuperUserRole(),
              icon: 'desktop-medical',
              badge: this.getBadgeValue('total'),
              items: [
                {
                  label: $localize`Import Log`,
                  icon: 'list-dropdown',
                  routerLink: ['master-data', 'external-system-import-logs'],
                  visible:
                    this.mainDataStore.admin() &&
                    (this.appConfig.configuration?.featureFlags.ExternalSystemHl7 ||
                      this.appConfig.configuration?.featureFlags.ExternalSystemCareCoach ||
                      this.appConfig.configuration?.featureFlags.ExternalSystemPolypoint ||
                      this.appConfig.configuration?.featureFlags.ExternalSystemLobos),
                },
                {
                  label: $localize`Aktivitäten Log`,
                  icon: 'notes-medical',
                  routerLink: ['master-data', 'external-system-activity-logs'],
                  visible:
                    this.appConfig.configuration?.featureFlags.ExternalSystemCareCoach ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemRestCaseActivityHints,
                },
                {
                  label: $localize`Überwachung`,
                  icon: 'bell',
                  routerLink: ['master-data', 'external-system-monitors'],
                  visible: this.mainDataStore.admin(),
                },
                {
                  label: $localize`Polypoint`,
                  visible: this.appConfig.configuration?.featureFlags.ExternalSystemPolypoint,
                  icon: 'desktop-medical',
                  badge: this.getBadgeValue(
                    'polypointGroupMappings',
                    'polypointCategoryMappings',
                    'polypointShiftMappings'
                  ),
                  items: [
                    {
                      label: $localize`Gruppen`,
                      icon: 'cubes',
                      routerLink: ['master-data', 'polypoint-groups'],
                      visible: this.appConfig.configuration?.featureFlags.ExternalSystemPolypoint,
                      badge: this.getBadgeValue('polypointGroupMappings'),
                    },
                    {
                      label: $localize`Kategorien`,
                      icon: 'list-dropdown',
                      routerLink: ['master-data', 'polypoint-categories'],
                      visible: this.appConfig.configuration?.featureFlags.ExternalSystemPolypoint,
                      badge: this.getBadgeValue('polypointCategoryMappings'),
                    },
                    {
                      label: $localize`Ikonen`,
                      // @ts-expect-error this dirty hack is need to use tacs3 custom icons
                      icon: ['tacs3', 'record'] as string,
                      routerLink: ['master-data', 'polypoint-shifts'],
                      visible: this.appConfig.configuration?.featureFlags.ExternalSystemPolypoint,
                      badge: this.getBadgeValue('polypointShiftMappings'),
                    },
                  ],
                },
                {
                  label: $localize`Organisationseinheitenmapping`,
                  icon: 'list-dropdown',
                  routerLink: ['master-data', 'external-system-organisational-units'],
                  visible:
                    this.appConfig.configuration?.featureFlags.ExternalSystemHl7 ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemCareCoach ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemLobos ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemPolypoint ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemFhir,
                  badge: this.getBadgeValue('externalSystemOrganisationalUnitMappings'),
                },
                {
                  label: $localize`Aktivitätenmapping`,
                  icon: 'notes-medical',
                  routerLink: ['master-data', 'external-system-activities'],
                  visible:
                    this.appConfig.configuration?.featureFlags.ExternalSystemCareCoach ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemRestCaseActivityHints,
                  badge: this.getBadgeValue('externalSystemActivityMappings'),
                },
                {
                  label: $localize`Tarifstufenmapping`,
                  icon: 'list-dropdown',
                  routerLink: ['master-data', 'external-system-tariff-levels'],
                  visible:
                    this.appConfig.configuration?.featureFlags.ExternalSystemHl7 ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemCareCoach,
                  badge: this.getBadgeValue('externalSystemTariffLevelMappings'),
                },
                {
                  label: $localize`Aufenthaltsartmapping`,
                  icon: 'list-dropdown',
                  routerLink: ['master-data', 'external-system-types-of-stay'],
                  visible:
                    this.appConfig.configuration?.featureFlags.ExternalSystemHl7 ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemFhir,
                  badge: this.getBadgeValue('externalSystemTypeOfStayMappings'),
                },
                {
                  label: $localize`Versicherungsklassenmapping`,
                  icon: 'list-dropdown',
                  routerLink: ['master-data', 'external-system-insurance-classes'],
                  visible:
                    this.appConfig.configuration?.featureFlags.ExternalSystemHl7 ||
                    this.appConfig.configuration?.featureFlags.ExternalSystemFhir,
                  badge: this.getBadgeValue('externalSystemInsuranceClassMappings'),
                },
                {
                  label: $localize`Geschlechtmapping`,
                  icon: 'list-dropdown',
                  routerLink: ['master-data', 'external-system-genders'],
                  visible: this.appConfig.configuration?.featureFlags.ExternalSystemHl7,
                  badge: this.getBadgeValue('externalSystemGenderMappings'),
                },
              ],
            },
          ],
        },
        {
          separator: true,
          visible: !this.isLargeScreen(),
        },
        {
          label: $localize`VAR Healthcare Portal`,
          icon: 'var-healthcare-portal',
          visible: !this.isLargeScreen(),
        },
        {
          label: $localize`Benutzerhandbuch`,
          url: this.manualUrl(),
          icon: 'question-circle',
          visible: !this.isLargeScreen(),
        },
        {
          label: $localize`Sprache`,
          items: this.languageMenuItems,
          icon: 'language',
          visible: !this.isLargeScreen(),
        },
        {
          label: this.fullname,
          icon: 'user',
          visible: !this.isLargeScreen(),
          items: this.avatarItems(),
        },
      ] satisfies MenuItem[]
  );

  logout(): void {
    const idToken = this.oAuthService.getIdToken();
    window.localStorage.clear();
    this.oAuthService.logOut({
      redirectUri: window.location.href,
      id_token_hint: idToken,
    });
  }

  throwError(): void {
    throw new Error('Test Error');
  }

  private triggerSync(): void {
    this.httpClient.post('/api/BackgroundJob/tacs2importjob', {}).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: $localize`Synchronisation erfolgreich angestossen`,
          detail: $localize`Die aktuellen Daten sind in Kürze verfügbar.`,
        });
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: $localize`Fehler beim Anstossen der Synchronisierung`,
          detail: err.message,
        });
      },
    });
    this.menubar()?.hide();
  }

  openFeedbackDialog(): void {
    this.dialogService.open(FeedbackDialogComponent, {
      modal: true,
      header: $localize`Feedback`,
      closable: true,
      closeOnEscape: true,
    });
  }
}
